/* eslint-disable max-lines */
import { useState, useEffect } from 'react';
import useDebounce from 'components/hooks/useDebounce';
import { getDashboards } from 'common/services/metrics.service';
import { ALL_TEAMS } from 'common/constants/teams';
import { get, uniqBy, isEmpty } from 'lodash';
import { NewCustomReport } from './views/CustomReport';
import { EMPTY_DUMMY_ORG_LEVEL_TEAM_GOALS_DASHBOARD } from './views/CustomReport/consts';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { teamsSelector } from 'state/selectors/teams';
import AppRouter from 'common/AppRouter';

const useTeamGoalsDashboard = ({ filters, gitScopeToggle }) => {
  const history = useHistory();
  const currentTeams = useSelector(teamsSelector);
  const [customDashboardsFiltered, setCustomDashboardsFiltered] = useState([]);
  const [teamGoalsDashboard, setTeamGoalsDashboard] = useState({});
  const [loaded, setLoaded] = useState(false);

  const selectReportAndRedirect = teamId => {
    history.push({
      pathname: `${AppRouter.TEAM_GOALS.path}/${teamId}`,
      search: history.location.search
    });
  };

  const loadDashboards = async () => {
    setLoaded(false);
    const teamId = filters.globallySelectedTeams?.[0]?.id || ALL_TEAMS.id;
    let dashboardsResult;

    // Don't call getDashboards for old All Teams
    if (teamId === ALL_TEAMS.id) {
      dashboardsResult = [];
    } else {
      dashboardsResult = await getDashboards(teamId);
    }
    const dashboards = get(dashboardsResult, 'items', []);

    const teamGoalsDashboards = uniqBy(
      dashboards.filter(dashboard => dashboard?.team_id === teamId),
      ({ name }) => name
    );
    setTeamGoalsDashboard(
      teamGoalsDashboards?.[0] || EMPTY_DUMMY_ORG_LEVEL_TEAM_GOALS_DASHBOARD // if no dashboard found - go to All Teams
    );
    setLoaded(true);
  };

  const CustomReportComponent = NewCustomReport;

  const definitionToReport = definition => ({
    component: () => (
      <CustomReportComponent
        report={definition}
        filters={filters}
        articleId={definition.articleId}
        currentTeams={currentTeams}
        isDashboardNameValid={() => {}}
        gitScopeToggle={gitScopeToggle}
      />
    ),
    pmRequired: definition.type === 'pm',
    route: definition.id,
    ...definition
  });

  const initTeamGoalDashboard = () => {
    setCustomDashboardsFiltered(definitionToReport(teamGoalsDashboard));
  };

  useEffect(() => {
    const fetchData = async () => {
      await loadDashboards();
    };

    fetchData();
  }, [filters.globallySelectedTeams?.[0]?.id]);

  useEffect(() => {
    if (!isEmpty(teamGoalsDashboard)) {
      selectReportAndRedirect(
        teamGoalsDashboard?.team_id || teamGoalsDashboard?.route
      );
    }
  }, [teamGoalsDashboard]);

  useDebounce(
    () => {
      if (!isEmpty(teamGoalsDashboard)) {
        initTeamGoalDashboard();
      }
    },
    [
      history.location.pathname,
      teamGoalsDashboard,
      filters.globallySelectedTeams,
      filters.selectedTimeRanges,
      filters.selectedGranularity,
      gitScopeToggle
    ],
    300
  );

  return {
    loaded,
    customDashboardsFiltered
  };
};

export default useTeamGoalsDashboard;
